import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Clock, MapPin, Home, Plane, Edit, Trash2, Plus, ChevronRight, Target } from 'lucide-react';
import api from '../lib/api';

const formatDatum = (d) => {
  const date = new Date(d + 'T00:00:00');
  return date.toLocaleDateString('de-AT', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
};

const GameCard = ({ game, teamName, isHighlighted, isNext, isAdmin, onEdit, onDelete }) => {
  const now = new Date();
  const gameEnd = new Date(new Date(`${game.datum}T${game.zeit}:00`).getTime() + 2 * 3600000);
  const hasResult = game.ergebnis_heim != null && game.ergebnis_gast != null;
  const status = hasResult ? 'abgeschlossen' : (now > gameEnd ? 'abgeschlossen' : 'offen');

  return (
    <Card className={`card-surface transition-all ${isHighlighted ? 'border-accent ring-1 ring-accent/30' : status === 'abgeschlossen' && !hasResult ? 'opacity-70' : ''}`} data-testid={`game-card-${game.game_id}`}>
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center gap-1.5 text-sm">
                <Calendar className="w-3.5 h-3.5 text-text-secondary" />
                <span className="font-medium">{formatDatum(game.datum)}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm">
                <Clock className="w-3.5 h-3.5 text-text-secondary" />
                <span>{game.zeit} Uhr</span>
              </div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-heading font-bold uppercase">{game.gegner}</span>
              {isNext && <Badge className="bg-accent/20 text-accent text-xs">Nächstes Spiel</Badge>}
            </div>
            {hasResult && (
              <div className="flex items-center gap-2 mb-2" data-testid={`game-result-${game.game_id}`}>
                <span className={`text-xl font-heading font-black ${game.ergebnis_heim > game.ergebnis_gast ? 'text-green-400' : game.ergebnis_heim < game.ergebnis_gast ? 'text-red-400' : 'text-yellow-400'}`}>
                  {game.heim
                    ? `${game.ergebnis_heim} : ${game.ergebnis_gast}`
                    : `${game.ergebnis_gast} : ${game.ergebnis_heim}`
                  }
                </span>
                <span className="text-xs text-text-secondary">
                  ({game.heim ? (teamName || 'Heim') : game.gegner} : {game.heim ? game.gegner : (teamName || 'Gast')})
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-3 text-sm text-text-secondary">
              <div className="flex items-center gap-1">
                {game.heim ? <Home className="w-3.5 h-3.5 text-accent" /> : <Plane className="w-3.5 h-3.5 text-primary" />}
                <span className={game.heim ? 'text-accent' : 'text-primary'}>{game.heim ? 'Heim' : 'Auswärts'}</span>
              </div>
              <div className="flex items-center gap-1">
                <MapPin className="w-3.5 h-3.5" />
                <span>{game.ort}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Badge className={hasResult ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : status === 'abgeschlossen' ? 'bg-red-500/20 text-red-400 border-red-500/30' : 'bg-green-500/20 text-green-400 border-green-500/30'}>
              {hasResult ? 'Ergebnis' : status === 'abgeschlossen' ? 'Abgeschlossen' : 'Offen'}
            </Badge>
            {isAdmin && (
              <>
                <Button size="sm" variant="ghost" onClick={() => onEdit(game)} data-testid={`edit-game-btn-${game.game_id}`}><Edit className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" className="text-red-400" onClick={() => onDelete(game)} data-testid={`delete-game-btn-${game.game_id}`}><Trash2 className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const SpielplanPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gamesLoading, setGamesLoading] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [editForm, setEditForm] = useState({ datum: '', zeit: '', gegner: '', heim: true, ort: '', ergebnis_heim: '', ergebnis_gast: '' });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addForm, setAddForm] = useState({ datum: '', zeit: '20:00', gegner: '', heim: true, ort: 'House of Darts' });
  const [view, setView] = useState('spielplan');
  const [stats, setStats] = useState([]);
  const [category, setCategory] = useState('edart'); // 'edart' or 'steel'

  const isSteel = (team) => (team.league || '').toLowerCase().includes('steel');
  const edartTeams = teams.filter(t => !isSteel(t));
  const steelTeams = teams.filter(t => isSteel(t));

  const fetchTeams = async () => {
    try {
      const [teamsRes, statsRes] = await Promise.all([
        api.get('/schedule/teams'),
        api.get('/schedule/statistics')
      ]);
      setTeams(teamsRes.data);
      setStats(statsRes.data);
      if (!selectedTeamId && teamsRes.data.length > 0) {
        const edart = teamsRes.data.filter(t => !(t.league || '').toLowerCase().includes('steel'));
        setSelectedTeamId(edart.length > 0 ? edart[0].team_id : teamsRes.data[0].team_id);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTeams(); }, []);

  const fetchGames = async () => {
    if (!selectedTeamId) return;
    setGamesLoading(true);
    try {
      const res = await api.get(`/schedule/teams/${selectedTeamId}/games`);
      setGames(res.data);
    } catch (e) { console.error(e); }
    finally { setGamesLoading(false); }
  };

  useEffect(() => { fetchGames(); }, [selectedTeamId]);

  const selectedTeam = teams.find(t => t.team_id === selectedTeamId);

  const getNextGame = () => {
    const now = new Date();
    return games.find(g => new Date(`${g.datum}T${g.zeit}:00`) >= now);
  };

  const nextGame = getNextGame();

  const handleEdit = (game) => {
    setEditGame(game);
    setEditForm({
      datum: game.datum, zeit: game.zeit, gegner: game.gegner,
      heim: game.heim, ort: game.ort,
      ergebnis_heim: game.ergebnis_heim ?? '', ergebnis_gast: game.ergebnis_gast ?? ''
    });
  };

  const handleSaveEdit = async () => {
    if (!editGame) return;
    try {
      const payload = { ...editForm };
      payload.ergebnis_heim = payload.ergebnis_heim !== '' ? parseInt(payload.ergebnis_heim) : null;
      payload.ergebnis_gast = payload.ergebnis_gast !== '' ? parseInt(payload.ergebnis_gast) : null;
      await api.put(`/admin/schedule/games/${editGame.game_id}`, payload);
      toast.success('Spiel aktualisiert');
      setEditGame(null);
      fetchGames();
      fetchTeams();
    } catch (e) { toast.error('Fehler beim Speichern'); }
  };

  const handleAddGame = async () => {
    if (!addForm.datum || !addForm.gegner) { toast.error('Datum und Gegner erforderlich'); return; }
    try {
      await api.post('/admin/schedule/games', { ...addForm, team_id: selectedTeamId });
      toast.success('Spiel hinzugefügt');
      setAddDialogOpen(false);
      setAddForm({ datum: '', zeit: '20:00', gegner: '', heim: true, ort: 'House of Darts' });
      fetchGames();
      fetchTeams();
    } catch (e) { toast.error('Fehler'); }
  };

  const handleDeleteGame = async (game) => {
    if (!window.confirm(`Spiel gegen "${game.gegner}" am ${game.datum} wirklich löschen?`)) return;
    try {
      await api.delete(`/admin/schedule/games/${game.game_id}`);
      toast.success('Spiel gelöscht');
      fetchGames();
      fetchTeams();
    } catch (e) { toast.error('Fehler'); }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="spielplan-page">
      <section className="section-padding bg-surface">
        <div className="container-max">
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-4">
            <span className="text-primary">Spiel</span>plan
          </h1>
          <p className="text-text-secondary max-w-2xl">
            Alle Spiele unserer Mannschaften auf einen Blick.
          </p>
          {/* View Toggle */}
          <div className="flex gap-2 mt-6">
            <button onClick={() => setView('spielplan')}
              className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'spielplan' ? 'bg-primary text-white' : 'bg-white/5 text-text-secondary hover:bg-white/10'}`}
              data-testid="view-spielplan">
              Spielplan
            </button>
            <button onClick={() => setView('statistik')}
              className={`px-4 py-2 rounded-sm text-sm font-medium transition-all ${view === 'statistik' ? 'bg-primary text-white' : 'bg-white/5 text-text-secondary hover:bg-white/10'}`}
              data-testid="view-statistik">
              Saison-Statistik
            </button>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-max">
          {loading ? (
            <div className="space-y-4">{[1, 2, 3].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-6"><div className="h-12 bg-white/10 rounded" /></CardContent></Card>)}</div>
          ) : view === 'statistik' ? (
            /* STATISTIK VIEW */
            <div data-testid="statistik-view">
              {(() => {
                const steelStats = stats.filter(s => s.is_steel);
                const edartStats = stats.filter(s => !s.is_steel);
                return (
                  <div className="space-y-10">
                    {/* E-Dart Table */}
                    {edartStats.length > 0 && (
                      <div>
                        <h2 className="text-2xl font-heading font-bold uppercase mb-1">E-Dart <span className="text-primary">Tabelle</span></h2>
                        <p className="text-text-secondary text-sm mb-4">Punkte = Summe der eigenen Spielpunkte</p>
                        <Card className="card-surface overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10 text-text-secondary">
                                  <th className="text-left p-3 pl-4">#</th>
                                  <th className="text-left p-3">Mannschaft</th>
                                  <th className="text-left p-3">Liga</th>
                                  <th className="text-center p-3">Sp.</th>
                                  <th className="text-center p-3">S</th>
                                  <th className="text-center p-3">U</th>
                                  <th className="text-center p-3">N</th>
                                  <th className="text-center p-3">Eigene</th>
                                  <th className="text-center p-3">Gegner</th>
                                  <th className="text-center p-3 pr-4 font-bold text-primary">Pkt.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {edartStats.map((s, i) => (
                                  <tr key={s.team_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`stat-row-${s.team_id}`}>
                                    <td className="p-3 pl-4 font-bold text-text-secondary">{i + 1}</td>
                                    <td className="p-3 font-heading font-bold uppercase">{s.name}</td>
                                    <td className="p-3"><Badge variant="outline" className="border-accent/50 text-accent text-xs">{s.league}</Badge></td>
                                    <td className="p-3 text-center">{s.played}</td>
                                    <td className="p-3 text-center text-green-400">{s.wins}</td>
                                    <td className="p-3 text-center text-yellow-400">{s.draws}</td>
                                    <td className="p-3 text-center text-red-400">{s.losses}</td>
                                    <td className="p-3 text-center">{s.own_total}</td>
                                    <td className="p-3 text-center">{s.opp_total}</td>
                                    <td className="p-3 text-center pr-4 font-heading font-bold text-lg text-primary">{s.points}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>
                    )}
                    {/* Steel Dart Table */}
                    {steelStats.length > 0 && (
                      <div>
                        <h2 className="text-2xl font-heading font-bold uppercase mb-1">Steel Dart <span className="text-primary">Tabelle</span></h2>
                        <p className="text-text-secondary text-sm mb-4">Sieg = 2 Pkt. | Unentschieden = 1 Pkt. | Niederlage = 0 Pkt.</p>
                        <Card className="card-surface overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-white/10 text-text-secondary">
                                  <th className="text-left p-3 pl-4">#</th>
                                  <th className="text-left p-3">Mannschaft</th>
                                  <th className="text-left p-3">Liga</th>
                                  <th className="text-center p-3">Sp.</th>
                                  <th className="text-center p-3">S</th>
                                  <th className="text-center p-3">U</th>
                                  <th className="text-center p-3">N</th>
                                  <th className="text-center p-3 pr-4 font-bold text-primary">Pkt.</th>
                                </tr>
                              </thead>
                              <tbody>
                                {steelStats.map((s, i) => (
                                  <tr key={s.team_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`stat-row-${s.team_id}`}>
                                    <td className="p-3 pl-4 font-bold text-text-secondary">{i + 1}</td>
                                    <td className="p-3 font-heading font-bold uppercase">{s.name}</td>
                                    <td className="p-3"><Badge variant="outline" className="border-accent/50 text-accent text-xs">{s.league}</Badge></td>
                                    <td className="p-3 text-center">{s.played}</td>
                                    <td className="p-3 text-center text-green-400">{s.wins}</td>
                                    <td className="p-3 text-center text-yellow-400">{s.draws}</td>
                                    <td className="p-3 text-center text-red-400">{s.losses}</td>
                                    <td className="p-3 text-center pr-4 font-heading font-bold text-lg text-primary">{s.points}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </Card>
                      </div>
                    )}
                    {stats.length === 0 && (
                      <Card className="card-surface"><CardContent className="p-8 text-center"><p className="text-text-secondary">Noch keine Ergebnisse eingetragen.</p></CardContent></Card>
                    )}
                  </div>
                );
              })()}
            </div>
          ) : (
            <>
              {/* Category Tabs: E-Dart / Steel Dart */}
              <div className="flex gap-2 mb-4">
                <button onClick={() => { setCategory('edart'); const first = edartTeams[0]; if (first) setSelectedTeamId(first.team_id); }}
                  className={`px-5 py-2.5 rounded-sm text-sm font-heading font-bold uppercase transition-all ${category === 'edart' ? 'bg-accent text-white' : 'bg-white/5 text-text-secondary hover:bg-white/10'}`}
                  data-testid="category-edart">
                  E-Dart <span className="ml-1 text-xs font-normal opacity-70">({edartTeams.length})</span>
                </button>
                <button onClick={() => { setCategory('steel'); const first = steelTeams[0]; if (first) setSelectedTeamId(first.team_id); }}
                  className={`px-5 py-2.5 rounded-sm text-sm font-heading font-bold uppercase transition-all ${category === 'steel' ? 'bg-accent text-white' : 'bg-white/5 text-text-secondary hover:bg-white/10'}`}
                  data-testid="category-steel">
                  Steel Dart <span className="ml-1 text-xs font-normal opacity-70">({steelTeams.length})</span>
                </button>
              </div>

              {/* Team Tabs (filtered by category) */}
              <div className="mb-8 -mx-4 px-4 overflow-x-auto">
                <div className="flex gap-2 min-w-max pb-2">
                  {(category === 'edart' ? edartTeams : steelTeams).map(t => (
                    <button key={t.team_id} onClick={() => setSelectedTeamId(t.team_id)}
                      className={`px-4 py-2.5 rounded-sm text-sm font-medium whitespace-nowrap transition-all ${selectedTeamId === t.team_id ? 'bg-primary text-white' : 'bg-white/5 text-text-secondary hover:bg-white/10 hover:text-white'}`}
                      data-testid={`team-tab-${t.team_id}`}>
                      {t.name}{t.league && <span className="ml-1.5 text-xs opacity-70">{t.league}</span>}
                    </button>
                  ))}
                </div>
              </div>

              {selectedTeam && (
                <>
                  {/* Team Header + Add Button */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <Target className="w-6 h-6 text-primary" />
                      <div>
                        <h2 className="text-2xl font-heading font-bold uppercase">{selectedTeam.name}</h2>
                        <Badge variant="outline" className="border-accent text-accent">{selectedTeam.league}</Badge>
                      </div>
                    </div>
                    {isAdmin && (
                      <Button className="btn-primary" onClick={() => { setAddForm({ datum: '', zeit: '20:00', gegner: '', heim: true, ort: 'House of Darts' }); setAddDialogOpen(true); }} data-testid="add-game-btn">
                        <Plus className="w-4 h-4 mr-2" />Spiel
                      </Button>
                    )}
                  </div>

                  {/* Next Game */}
                  {nextGame ? (
                    <div className="mb-8">
                      <h3 className="text-sm font-heading font-bold uppercase text-accent mb-3 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4" />Nächstes Spiel
                      </h3>
                      <GameCard game={nextGame} teamName={selectedTeam?.name} isHighlighted isNext isAdmin={isAdmin} onEdit={handleEdit} onDelete={handleDeleteGame} />
                    </div>
                  ) : (
                    <Card className="card-elevated border-yellow-500/30 mb-8"><CardContent className="p-6 text-center"><p className="text-yellow-400 font-medium">Keine offenen Spiele mehr vorhanden</p></CardContent></Card>
                  )}

                  {/* All Games */}
                  <h3 className="text-sm font-heading font-bold uppercase text-text-secondary mb-3">Alle Spiele ({games.length})</h3>
                  {gamesLoading ? (
                    <div className="space-y-3">{[1, 2, 3].map(i => <Card key={i} className="card-surface animate-pulse"><CardContent className="p-5"><div className="h-16 bg-white/10 rounded" /></CardContent></Card>)}</div>
                  ) : (
                    <div className="space-y-3">
                      {games.map(game => <GameCard key={game.game_id} game={game} teamName={selectedTeam?.name} isHighlighted={false} isNext={nextGame?.game_id === game.game_id} isAdmin={isAdmin} onEdit={handleEdit} onDelete={handleDeleteGame} />)}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </section>

      {/* Edit Dialog */}
      <Dialog open={!!editGame} onOpenChange={() => setEditGame(null)}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>Spiel bearbeiten</DialogTitle></DialogHeader>
          {editGame && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Datum</Label><Input type="date" className="input-dark" value={editForm.datum} onChange={e => setEditForm({ ...editForm, datum: e.target.value })} /></div>
                <div className="space-y-2"><Label>Uhrzeit</Label><Input type="time" className="input-dark" value={editForm.zeit} onChange={e => setEditForm({ ...editForm, zeit: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Gegner</Label><Input className="input-dark" value={editForm.gegner} onChange={e => setEditForm({ ...editForm, gegner: e.target.value })} /></div>
              <div className="space-y-2"><Label>Ort</Label><Input className="input-dark" value={editForm.ort} onChange={e => setEditForm({ ...editForm, ort: e.target.value })} /></div>
              <div className="flex items-center gap-3">
                <Switch checked={editForm.heim} onCheckedChange={v => setEditForm({ ...editForm, heim: v })} />
                <Label>{editForm.heim ? 'Heimspiel' : 'Auswärtsspiel'}</Label>
              </div>
              <div className="space-y-2">
                <Label>Ergebnis</Label>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0" className="input-dark w-20 text-center" placeholder={editForm.heim ? 'Heim' : 'Gast'} value={editForm.ergebnis_heim} onChange={e => setEditForm({ ...editForm, ergebnis_heim: e.target.value })} data-testid="edit-ergebnis-heim" />
                  <span className="text-text-secondary font-bold">:</span>
                  <Input type="number" min="0" className="input-dark w-20 text-center" placeholder={editForm.heim ? 'Gast' : 'Heim'} value={editForm.ergebnis_gast} onChange={e => setEditForm({ ...editForm, ergebnis_gast: e.target.value })} data-testid="edit-ergebnis-gast" />
                  <span className="text-xs text-text-secondary">({editForm.heim ? `${selectedTeam?.name || 'Heim'} : ${editForm.gegner || 'Gast'}` : `${editForm.gegner || 'Gast'} : ${selectedTeam?.name || 'Heim'}`})</span>
                </div>
              </div>
              <Button onClick={handleSaveEdit} className="btn-primary w-full" data-testid="save-edit-game-btn">Speichern</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Game Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-surface border-white/10">
          <DialogHeader><DialogTitle>Neues Spiel hinzufügen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Datum *</Label><Input type="date" className="input-dark" value={addForm.datum} onChange={e => setAddForm({ ...addForm, datum: e.target.value })} data-testid="add-game-datum" /></div>
              <div className="space-y-2"><Label>Uhrzeit</Label><Input type="time" className="input-dark" value={addForm.zeit} onChange={e => setAddForm({ ...addForm, zeit: e.target.value })} /></div>
            </div>
            <div className="space-y-2"><Label>Gegner *</Label><Input className="input-dark" value={addForm.gegner} onChange={e => setAddForm({ ...addForm, gegner: e.target.value })} data-testid="add-game-gegner" /></div>
            <div className="space-y-2"><Label>Ort</Label><Input className="input-dark" value={addForm.ort} onChange={e => setAddForm({ ...addForm, ort: e.target.value })} /></div>
            <div className="flex items-center gap-3">
              <Switch checked={addForm.heim} onCheckedChange={v => setAddForm({ ...addForm, heim: v })} />
              <Label>{addForm.heim ? 'Heimspiel' : 'Auswärtsspiel'}</Label>
            </div>
            <Button onClick={handleAddGame} className="btn-primary w-full" data-testid="save-new-game-btn">Spiel hinzufügen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpielplanPage;
